import React, {forwardRef} from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {SIZES_TYPES, Sizes} from './constants';
import iconStyles from './icon.component.module.styl';
import MultiColor, {ICONS_SVG} from './icons-multi-color-generated';

import type {IconSize} from './constants';
import type {CSSProperties, ComponentPropsWithoutRef} from 'react';


const classNames = withCssModule(iconStyles);

const styles = (size?: IconSize, style?: CSSProperties) => (
    size
        ? {
                fontSize: SIZES_TYPES[size] || undefined,
                ...style,
            }
        : style
);

const customStyles = (size?: IconSize, style?: CSSProperties): {
    fontSize: React.CSSProperties['fontSize'],
    fontWeight: React.CSSProperties['fontWeight'],
} => ({
    fontSize: (size ? SIZES_TYPES[size] : SIZES_TYPES[Sizes.Size_16]) || undefined,
    fontWeight: 400,
    ...style,
});


const getNativeProps = ({size, type, mix, unit, block, fixedBox, loader, rootProps,
    ...nativeProps
}: IconProps): NativeIconProps => nativeProps;

type NativeIconProps = ComponentPropsWithoutRef<'i'>;

export interface IconProps extends NativeIconProps {
    /**
     * SingleColor or MultiColor icon type
     */
    type: string,
    /**
     * Unit style variant
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     * */
    unit?: boolean,

    /**
     * Icon size
     */
    size?: IconSize,

    /**
     * CSS rule `display: block`
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    block?: boolean,

    /**
     * Show icon in block 14 x 14px
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    fixedBox?: boolean,

    /**
     * Icon with loader
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    loader?: boolean,

    /**
     * Custom classes
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,
    /**
     * Extra props for the root HTML element
     * @deprecated It will be removed in 6 months 09-15-2025. pass native props directly
     * */
    rootProps?: NativeIconProps,

    /**
     * Ref to the HTML element
     */
    ref?: React.Ref<HTMLElement>,
}

const MultiColorValues = Object.fromEntries(
    Object
        .values(MultiColor)
        .map(value => [value, true]),
);

function isMultiColorIcon(type: string): type is keyof typeof ICONS_SVG {
    return MultiColorValues[type];
}

/**
 * Shows an icon
 *
 * @see https://storybook.dev.pitchbookdata.com/?path=/docs/fundamentals-icons--documentation
 * */
export const Icon = forwardRef<HTMLElement, IconProps>(function Icon(props, ref) {
    const {
        type,
        unit,
        size,
        block,
        fixedBox,
        loader,
        mix,
        className,
        rootProps,
    } = props;

    const isMultiColor = isMultiColorIcon(type);

    const nativeProps: NativeIconProps = {
        ...rootProps,
        ...getNativeProps(props),
        'aria-hidden': props['aria-hidden'] ?? true,
        'className': classNames(
            mix,
            className,
            rootProps?.className,
            {
                ['icon-' + type]: !isMultiColor,
                ['multicolor-icon-' + type]: isMultiColor,
                'icon_wrapper': fixedBox,
                'icon_unit': unit,
                'icon_block': block,
                'icon_with-loader': loader,
            },
        ),
    };


    if (isMultiColor) {
        const Svg = ICONS_SVG[type] as React.FC<NativeIconProps & {ref?: React.Ref<HTMLElement>}>;
        return (
            <Svg
                {...nativeProps}
                style={customStyles(size, props.style)}
                ref={ref}
            />
        );
    } else {
        return <i {...nativeProps} style={styles(size, props.style)} ref={ref}/>;
    }
});

Icon.displayName = 'Icon';
